/// <amd-module name="Wiki/WitQuerySelector/WitQuerySelectorPanel" />
import * as React from "react";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Panel } from "VSSUI/Panel";

import { ITfsPageService } from "Tfs/Platform/Page";

import { QueryProperties } from "Wiki/WitQuerySelector/Helper";
import * as Resources from "Wiki/WitQuerySelector/Resources";
import { WitQuerySelector } from "Wiki/WitQuerySelector/WitQuerySelector";

export interface IWitQuerySelectorPanelProps {
    onInsertClick: (queryId: string) => void;
}

export interface IWitQuerySelectorPanelState {
    showPanel: boolean;
    selectedQueryId?: string;
    queryProperties?: QueryProperties;
}

export class WitQuerySelectorPanel extends VssComponent<IWitQuerySelectorPanelProps, IWitQuerySelectorPanelState> {
    private static readonly s_filterBarItemInputClassName = "query-filterbaritem-input";
    private static readonly s_defaultActiveElement = "." + WitQuerySelectorPanel.s_filterBarItemInputClassName;
    private _projectId: string | undefined;

    constructor(props: IWitQuerySelectorPanelProps, context: IVssComponentContext) {
        super(props, context);
        const tfsPageData = context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        this._projectId = tfsPageData && tfsPageData.project && tfsPageData.project.id;
        this.state = { showPanel: true };
    }

    public render(): JSX.Element | null {
        const { selectedQueryId, showPanel } = this.state;
        if (!showPanel) {
            return null;
        }

        const footerButtons: IButtonProps[] = [
            {
                onClick: this._onCancelButtonClick,
                text: Resources.CancelButtonText
            },
            {
                disabled: !selectedQueryId,
                onClick: this._onInsertButtonClick,
                primary: true,
                text: Resources.InsertButtonText
            }
        ];

        return (
            <Panel
                titleProps={{ text: Resources.PanelTitle }}
                showSeparator={true}
                defaultActiveElement={WitQuerySelectorPanel.s_defaultActiveElement}
                onDismiss={this._onCancelButtonClick}
                footerButtonProps={footerButtons}
                size={ContentSize.Large}
            >
                <WitQuerySelector
                    onQuerySelected={this._onQuerySelected}
                    resetQuery={this._resetQuery}
                    className="flex-column flex-grow"
                    filterBarItemInputClassName={WitQuerySelectorPanel.s_filterBarItemInputClassName}
                />
            </Panel>
        );
    }

    private _onQuerySelected = (selectedQueryId: string, queryProperties: QueryProperties, insertQuery?: boolean): void => {
        if (insertQuery) {
            this._insertQuery(selectedQueryId, queryProperties);
        } else {
            this.setState({ selectedQueryId, queryProperties });
        }
    };

    private _resetQuery = (): void => {
        this.setState({ selectedQueryId: undefined });
    };

    private _onCancelButtonClick = (): void => {
        this._publish("WitQuerySelectorPanelCancelClick", {});
        this._hidePanel();
    };

    private _onInsertButtonClick = (): void => {
        const { selectedQueryId, queryProperties } = this.state;
        if (selectedQueryId) {
            this._insertQuery(selectedQueryId, queryProperties);
        }
    };

    private _insertQuery(selectedQueryId: string, queryProperties: QueryProperties = {}): void {
        this._publish("WitQuerySelectorPanelInsertClick", { queryId: selectedQueryId, ...queryProperties });
        this.props.onInsertClick(selectedQueryId);
        this._hidePanel();
    }

    private _hidePanel(): void {
        this.setState({ showPanel: false });
    }

    private _publish(feature: string, properties: { [key: string]: any }): void {
        const service = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        service.publishEvent("Wiki", feature, { projectId: this._projectId, ...properties });
    }
}

VssComponent.register("Wiki.WitQuerySelectorPanelComponent", WitQuerySelectorPanel);
